import React, {FC, useCallback, useContext, useState} from 'react';
import {Checkbox} from 'semantic-ui-react';

import {
  useCreateAccessTokenMutation,
  useRevokeAccessTokenMutation,
} from '../../generated/graphql';
import * as ViewActions from '../../state/views/actions';
import {useViewRefAction} from '../../state/views/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {useReportProjects} from '../../util/report';
import {reportView} from '../../util/urls/shared';
import * as S from './ShareReportTrigger.styles';
import {ShareReportTriggerContext} from './ShareReportTriggerContextProvider';

export const MagicLinkSection: FC = () => {
  const {canChangeSharingSettings, currentAccessToken, viewRef, view} =
    useContext(ShareReportTriggerContext);

  const {origin, host} = window.location;
  const pathname = reportView({
    entityName: view.project?.entityName ?? '',
    projectName: view.project?.name ?? '',
    reportID: view.id ?? '',
    reportName: view.displayName,
  });
  const accessTokenQS =
    currentAccessToken != null
      ? `?accessToken=${currentAccessToken.token}`
      : '';

  const [isMagicLinkAnimated, setIsMagicLinkAnimated] = useState(false);
  const animateMagicLink = useCallback(() => {
    setIsMagicLinkAnimated(false);
    setTimeout(() => {
      setIsMagicLinkAnimated(true);
    });
  }, []);

  const addAccessToken = useViewRefAction(viewRef, ViewActions.addAccessToken);
  const removeAccessToken = useViewRefAction(
    viewRef,
    ViewActions.removeAccessToken
  );

  const projectSpecifiers = useReportProjects(viewRef);
  const [createAccessToken] = useCreateAccessTokenMutation({
    variables: {
      projects: projectSpecifiers,
      viewId: view.id!,
    },
    context: propagateErrorsContext(),
  });
  const [revokeAccessToken] = useRevokeAccessTokenMutation();

  const [isCreatingAccessToken, setIsCreatingAccessToken] = useState(false);
  const [isRevokingAccessToken, setIsRevokingAccessToken] = useState(false);

  const createLink = useCallback(async () => {
    if (isCreatingAccessToken || currentAccessToken != null) {
      return;
    }
    window.analytics?.track?.('Report View Only Link Created', {
      reportId: view.cid,
    });
    setIsCreatingAccessToken(true);
    const result = await createAccessToken();
    const accessToken = result.data?.createAccessToken?.accessToken;
    if (accessToken != null) {
      addAccessToken(accessToken);
    }
    setIsCreatingAccessToken(false);
  }, [
    isCreatingAccessToken,
    currentAccessToken,
    createAccessToken,
    addAccessToken,
    view.cid,
  ]);

  const deactivateLink = useCallback(async () => {
    if (isRevokingAccessToken || currentAccessToken == null) {
      return;
    }
    window.analytics?.track?.('Report Deactivate View Only Link Clicked', {
      reportId: view.cid,
    });
    setIsRevokingAccessToken(true);
    const result = await revokeAccessToken({
      variables: {
        token: currentAccessToken.token,
      },
    });
    if (result.data?.revokeAccessToken?.success) {
      removeAccessToken(currentAccessToken.token);
    }
    setIsRevokingAccessToken(false);
  }, [
    isRevokingAccessToken,
    currentAccessToken,
    removeAccessToken,
    revokeAccessToken,
    view.cid,
  ]);

  const handleToggleMagicLink = async () => {
    if (!canChangeSharingSettings) {
      return;
    }
    if (currentAccessToken != null) {
      setIsRevokingAccessToken(true);
      await deactivateLink();
      setIsRevokingAccessToken(false);
    } else {
      setIsCreatingAccessToken(true);
      await createLink();
      setIsCreatingAccessToken(false);
    }
  };

  return (
    <>
      <S.RowWrapper>
        <S.MagicLinkIconTextWrapper>
          <S.GrayCircleWBIcon name="link" />
          <S.MagicLinkText disabled={currentAccessToken == null}>
            Anyone with the magic link can view
          </S.MagicLinkText>
        </S.MagicLinkIconTextWrapper>
        <S.Toggle>
          <S.MagicLinkStatusTextWrapper disabled={currentAccessToken == null}>
            {currentAccessToken == null ? 'Off' : 'On'}
          </S.MagicLinkStatusTextWrapper>
          <S.Tooltip
            on="hover"
            inverted
            position="top center"
            size="mini"
            content={
              canChangeSharingSettings
                ? ''
                : 'Only team admin or author can deactivate magic link'
            }
            hidden={
              canChangeSharingSettings ||
              (currentAccessToken == null && !canChangeSharingSettings)
            }
            trigger={
              <Checkbox
                toggle
                onChange={handleToggleMagicLink}
                checked={currentAccessToken != null}
                disabled={isCreatingAccessToken || isRevokingAccessToken}
              />
            }
          />
        </S.Toggle>
      </S.RowWrapper>
      {(currentAccessToken != null || !canChangeSharingSettings) && (
        <S.CopyableLinkWrapper>
          {currentAccessToken != null && (
            <S.CopyableLink
              isAnimated={isMagicLinkAnimated}
              text={`${host}${pathname}${accessTokenQS}`}
              copyText={`${origin}${pathname}${accessTokenQS}`}
              toastText="Link copied"
              onClick={() => {
                animateMagicLink();
                window.analytics?.track?.('Report View Only Link Copied', {
                  reportId: view.cid,
                });
              }}
            />
          )}
          {currentAccessToken == null && !canChangeSharingSettings && (
            <S.SharingDisabledMessageWrapper>
              Sharing not available for this report. Ask team admin or author to
              turn on magic link to enable sharing.
            </S.SharingDisabledMessageWrapper>
          )}
        </S.CopyableLinkWrapper>
      )}
    </>
  );
};
